import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { SelectedLocationService, SiteLocation } from '@app/data/services/locationServices';
import { ChartService } from '@app/data/services/chartService';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { EChartsOption } from 'echarts';
import { getKaupapaChartConfig, KaupapaChartData } from './rongo-kaupapa-chart-helpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown } from '@app/widgets/helpers/organisation-breakdown';

@Component({
  selector: 'app-rongo-kaupapa-chart',
  templateUrl: './rongo-kaupapa-chart.component.html',
  styleUrls: ['./rongo-kaupapa-chart.component.scss'],
})
export class RongoKaupapaChartComponent implements OnInit {
  chartTitle: string = 'Rongo Kaupapa';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-kaupapa';
  startDate: any = '';
  endDate: any = '';
  location: SiteLocation | null = null;
  data: KaupapaChartData[] | [] = [];
  availableOptions: string[] = [];
  selectedOption: string = 'All';
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  site: Site | null = null;
  breakdown: RongoSourceGroup<KaupapaChartData[]>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService,
    private selectedLocationService: SelectedLocationService
  ) {}

  ngOnInit() {
    this.selectedLocationService.subscribe(this.handleLocationUpdate);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }

  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    this.chartService
      .getKaupapaData(this.startDate, this.endDate, this.location ? this.location.id : null, 'organisation')
      .subscribe(
        (data: RongoReportingData<KaupapaChartData[]>) => {
          const { config, data: kaupapaData, breakdown } = data;
          this.chartTitle = config.title;
          this.chartSubtitle = config.subTitle || '';
          this.data = kaupapaData;
          this.breakdown = breakdown?.groups || null;
          this.noData = kaupapaData.length === 0;
          this.availableOptions = ['All', ...Array.from(new Set(kaupapaData.map((item) => item.visitPurpose)))];
          this.selectedOption = 'All';
          this.setChartData();
        },
        (error: any) => {
          console.error('Error fetching data:', error);
        }
      );
  };

  setChartData = () => {
    const filterData = (data: KaupapaChartData[]) =>
      this.selectedOption === 'All' ? data : data.filter((item) => item.visitPurpose === this.selectedOption);

    this.chartOptions = withOrganisationBreakdown(
      getKaupapaChartConfig(filterData(this.data as KaupapaChartData[])),
      this.breakdown,
      {
        buildGroupOption: (d) => getKaupapaChartConfig(filterData(d)),
        flatten: (d: KaupapaChartData[]) => {
          const totals: Record<string, number> = {};
          d.forEach((item) => {
            totals[item.projectName] = (totals[item.projectName] || 0) + item.totalObservations;
          });
          return Object.entries(totals).map(([name, value]) => ({ name, value }));
        },
      }
    );
  };

  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };

  handleLocationUpdate = (location: SiteLocation | null) => {
    if (!location && !this.location) return;
    if (location?.id != this.location?.id) {
      this.location = location;
      this.setData();
    }
  };

  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }

  handlePurposeChange(event: MatSelectChange): void {
    this.setChartData();
  }
}
